import { createIdentityFilesBatch } from './identity_services';

export const ALLOWED_UPLOAD_EXTENSIONS = new Set(['.md', '.json', '.txt']);
export const MAX_UPLOAD_BYTES = 1 * 1024 * 1024;
/** Keep under backend MAX_BATCH_FILES (100). */
export const BATCH_CHUNK_SIZE = 10;

export function extOf(name) {
  const i = (name || '').lastIndexOf('.');
  if (i < 0) return '';
  return name.slice(i).toLowerCase();
}

/** Strip .. / empty segments; keep forward slashes. */
export function normalizeRelativePath(rel) {
  return String(rel || '')
    .replace(/\\/g, '/')
    .split('/')
    .filter((p) => p && p !== '.' && p !== '..')
    .join('/');
}

/**
 * Build logical identity path from a browser File + current explorer folder.
 * Folder picks expose webkitRelativePath (e.g. "MyTree/self/SOUL.md").
 */
export function buildUploadPath(file, folderPrefix = '') {
  const relative = normalizeRelativePath(file.webkitRelativePath || file.name);
  const prefix = folderPrefix ? folderPrefix.replace(/\/+$/, '') : '';
  if (!relative) return '';
  return prefix ? `${prefix}/${relative}` : relative;
}

export function classifyUploadFile(file, folderPrefix = '') {
  const path = buildUploadPath(file, folderPrefix);
  const ext = extOf(path || file.name);
  if (!path) {
    return { file, path: '', ok: false, reason: 'Empty path' };
  }
  if (!ALLOWED_UPLOAD_EXTENSIONS.has(ext)) {
    return { file, path, ok: false, reason: `Extension ${ext || '(none)'} not allowed` };
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return { file, path, ok: false, reason: `Exceeds ${MAX_UPLOAD_BYTES / 1024} KB` };
  }
  return { file, path, ok: true, reason: null };
}

export function classifyUploadFiles(fileList, folderPrefix = '') {
  return Array.from(fileList || []).map((f) => classifyUploadFile(f, folderPrefix));
}

function chunkArray(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function isGcsForbidden(status, data) {
  return status === 403 || data?.code === 'gcs_forbidden';
}

/**
 * Upload accepted items via POST /api/identity/files/batch/ (chunked).
 *
 * onItemStatus({ path, status, error }) — status: uploading | ok | error | skipped
 * onProgress({ done, total })
 */
export async function uploadIdentityItems(items, { onProgress, onItemStatus } = {}) {
  const accepted = items.filter((i) => i.ok);
  const total = accepted.length;
  let done = 0;
  const outcomes = [];

  const mark = (path, status, error = null) => {
    onItemStatus?.({ path, status, error });
  };

  if (!total) {
    return { total: 0, succeeded: 0, failed: 0, aborted: false, outcomes: [] };
  }

  // Read file contents first (local), then batch POST.
  const payloads = [];
  for (const item of accepted) {
    mark(item.path, 'uploading');
    try {
      const content = await item.file.text();
      payloads.push({ path: item.path, content, item });
    } catch (err) {
      done += 1;
      const error = err?.message || 'Failed to read file';
      mark(item.path, 'error', error);
      outcomes.push({ path: item.path, ok: false, error });
      onProgress?.({ done, total, path: item.path, ok: false, error });
    }
  }

  const chunks = chunkArray(payloads, BATCH_CHUNK_SIZE);
  let aborted = false;

  for (const chunk of chunks) {
    if (aborted) {
      chunk.forEach(({ path }) => {
        done += 1;
        const error = 'Skipped after GCS permission error';
        mark(path, 'skipped', error);
        outcomes.push({ path, ok: false, error, code: 'aborted' });
        onProgress?.({ done, total, path, ok: false, error });
      });
      continue;
    }

    try {
      const body = chunk.map(({ path, content }) => ({ path, content }));
      const { data, status } = await createIdentityFilesBatch(body);

      if (isGcsForbidden(status, data) && (data?.created_count || 0) === 0) {
        aborted = true;
        const detail = data?.detail || data?.errors?.[0]?.detail || 'GCS permission denied';
        chunk.forEach(({ path }, idx) => {
          done += 1;
          const errItem = data?.errors?.find((e) => e.index === idx);
          const error = errItem?.detail || detail;
          const code = errItem?.code || 'gcs_forbidden';
          mark(path, code === 'aborted' ? 'skipped' : 'error', error);
          outcomes.push({ path, ok: false, error, code });
          onProgress?.({ done, total, path, ok: false, error });
        });
        continue;
      }

      const okByPath = new Map((data?.results || []).map((r) => [r.path, r]));
      const errByIndex = new Map((data?.errors || []).map((e) => [e.index, e]));

      chunk.forEach(({ path }, idx) => {
        done += 1;
        const err = errByIndex.get(idx);
        if (err) {
          const statusLabel = err.code === 'aborted' ? 'skipped' : 'error';
          if (err.code === 'gcs_forbidden' || err.code === 'aborted') aborted = true;
          mark(path, statusLabel, err.detail);
          outcomes.push({ path, ok: false, error: err.detail, code: err.code });
          onProgress?.({ done, total, path, ok: false, error: err.detail });
          return;
        }
        const result = okByPath.get(path);
        mark(path, 'ok');
        outcomes.push({ path, ok: true, result });
        onProgress?.({ done, total, path, ok: true, result });
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Batch upload failed';
      const code = err?.response?.data?.code;
      if (isGcsForbidden(err?.response?.status, err?.response?.data)) {
        aborted = true;
      }
      chunk.forEach(({ path }) => {
        done += 1;
        mark(path, aborted ? 'error' : 'error', detail);
        outcomes.push({ path, ok: false, error: detail, code });
        onProgress?.({ done, total, path, ok: false, error: detail });
      });
      if (aborted) {
        // remaining chunks marked in next loop iterations
      }
    }
  }

  return {
    total,
    succeeded: outcomes.filter((o) => o.ok).length,
    failed: outcomes.filter((o) => !o.ok).length,
    aborted,
    outcomes,
  };
}
